import React, { useEffect } from "react";
import { styled } from "@mui/system";
import { Container, Typography, Grid, TextField, Checkbox, Button, FormGroup } from "@mui/material";
import FormControlLabel from '@mui/material/FormControlLabel';
import globals from "../globals";
import TextFieldRegister from "../components/register/text_field";
import PasswordFieldRegister from "../components/register/passwd_field";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import WarningIcon from '@mui/icons-material/Warning';
import { triggerGTM } from "../google_tag/gtm";


const SignUpButton = styled(Button)(() => ({
    color: globals.colors.backgroundColor,
    backgroundColor: globals.colors.lightRed,
    '&:hover': {
        backgroundColor: globals.colors.darkRed,
        color: globals.colors.backgroundColor,
    },
    '&.Mui-selected': {
        color: globals.colors.lightRed,
    },
}))

const RegisterPage = () => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectParam = searchParams.get("redirect");
    // Only follow same-site paths ("/webinar/x"), never "//evil.com" or full URLs.
    const redirectTo = redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
        ? redirectParam
        : "/dashboard";
    const { user } = useAuth();
    const [showError, setShowError] = React.useState(false)

    useEffect(() => {
        if (user) {
            navigate(redirectTo);
        }
    }, [user]);

    const [userData, setUserData] = React.useState({
        first_name: "",
        last_name: "",
        username: "",
        email: "",
        password: "",
        password_retype: "",
        role: "student",
        phone_number: "",
        underage: false,
        parent_email: "",
        description: "",
        subscribed_to_newsletter: false,
    });

    // Password matching validation
    const [passwordMatchError, setPasswordMatchError] = React.useState(false);

    // TexField validation
    // Name 
    const [name, setName] = React.useState("");
    const [nameError, setNameError] = React.useState(false);

    const handleNameChange = e => {
        setName(e.target.value);
        if (e.target.validity.valid) {
            setNameError(false);
        } else {
            setNameError(true);
        }
    };

    const [underaged, setUnderaged] = React.useState(false);

    const handleChange = (event) => {
        setUnderaged(event.target.checked);
    };

    const handleUserDataChange = (event) => {
        const newValue = (event.target.type === "checkbox") ? event.target.checked : event.target.value;
        const newUserData = {
            ...userData,
            [event.target.id]: newValue
        };

        setUserData(newUserData);

        // Check password matching when either password field changes
        if (event.target.id === 'password' || event.target.id === 'password_retype') {
            const password = event.target.id === 'password' ? newValue : newUserData.password;
            const passwordRetype = event.target.id === 'password_retype' ? newValue : newUserData.password_retype;

            if (passwordRetype && password !== passwordRetype) {
                setPasswordMatchError(true);
            } else {
                setPasswordMatchError(false);
            }
        }
    };



    const handleSubmit = (event) => {
        event.preventDefault();

        setShowError(false);
        // Check if passwords match before submission
        if (userData.password !== userData.password_retype) {
            setPasswordMatchError(true);
            return;
        }

        const data = {
            first_name: userData.first_name,
            last_name: userData.last_name,
            username: userData.username,
            email: userData.email,
            password: userData.password,
            role: userData.role,
            phone_number: userData.phone_number,
            underage: userData.underage,
            description: userData.description,
            subscribed_to_newsletter: userData.subscribed_to_newsletter,
        }
        if (userData.underage) {
            data.parent_email = userData.parent_email;
        }
        fetch(globals.apiUrl + "/auth/signup", {
            method: "POST",
            body: JSON.stringify(data),
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then(response => {
                if (response.status === 200) {
                    return response.json();
                }
                throw new Error(response.detail);

            })
            .then(data => {
                //console.log(data);
                triggerGTM('registration', 'register_button');
                const loginPath = redirectTo !== '/dashboard'
                    ? `/login?redirect=${encodeURIComponent(redirectTo)}`
                    : '/success-reg';
                navigate(loginPath);
            })
            .catch(error => {
                //console.log(error);
                setShowError(true);

            });

    };

    return (
        <Container>
            <Grid component="form" onSubmit={handleSubmit} container noValidate>
                <Grid container justifyContent={"center"} textAlign={"center"}>
                    <Typography sx={{ color: globals.colors.darkBlue, fontSize: 35, fontWeight: 'bold' }}>Създаване на профил</Typography>
                    <Grid item xs={12} sm={10} justifyContent={'center'} alignItems={'center'} display={'flex'} textAlign={'center'} sx={{ mt: 2 }}>
                        <TextFieldRegister onChange={handleUserDataChange} labelName={'Име'} idName="first_name" inputProps={{ pattern: "[A-Za-z А-Яа-я]+" }} helperText={"Името може да съдържа само малки и главни букви."} />
                    </Grid>
                    <Grid item xs={12} sm={10} justifyContent={'center'} alignItems={'center'} display={'flex'} textAlign={'center'} sx={{ mt: 2 }}>
                        <TextFieldRegister onChange={handleUserDataChange} labelName={'Фамилия'} idName="last_name" inputProps={{ pattern: "[A-Za-z А-Яа-я]+" }} helperText={"Фамилията може да съдържа само малки и главни букви."} />
                    </Grid>
                    <Grid item xs={12} sm={10} justifyContent={'center'} alignItems={'center'} display={'flex'} textAlign={'center'} sx={{ mt: 2 }}>
                        <TextFieldRegister onChange={handleUserDataChange} required labelName={'Потребителско име'} idName={'username'} inputProps={{ pattern: "[A-Za-z0-9_]+" }} helperText={"Потребителското име може да съдържа само малки и главни букви и долна черта."} />
                    </Grid>
                    <Grid item xs={12} sm={10} justifyContent={'center'} alignItems={'center'} display={'flex'} textAlign={'center'} sx={{ mt: 2 }}>
                        <TextFieldRegister onChange={handleUserDataChange} required labelName={'Имейл'} idName={'email'} inputProps={{ type: 'email' }} helperText={"Моля, въведете валиден имейл адрес."} />
                    </Grid>
                    <Grid item xs={12} sm={10} justifyContent={'center'} alignItems={'center'} display={'flex'} textAlign={'center'} sx={{ mt: 2 }}>
                        <FormControlLabel control={<Checkbox onChange={handleUserDataChange} id="underage" sx={{
                            color: globals.colors.lightRed, '&:hover': {
                                backgroundColor: 'transparent', // prevent ripple background
                                color: globals.colors.darkRed,  // change checkbox color on hover
                            },
                            '&.Mui-checked': {
                                color: globals.colors.darkRed, // keep it dark red when checked
                            },
                        }} />} label="Нямам навършени 18 години" />
                    </Grid>
                    {userData.underage ?
                        <Grid item xs={12} sm={10} justifyContent={'center'} alignItems={'center'} display={'flex'} textAlign={'center'} sx={{ mt: 2 }}>
                            <TextFieldRegister onChange={handleUserDataChange} required labelName={'Имейл адрес на родител'} idName={'parent_email'} inputProps={{ type: 'email' }} helperText={"Моля, въведете валиден имейл адрес."} />
                        </Grid> : null}
                    <Grid item xs={12} sm={10} justifyContent={'center'} alignItems={'center'} display={'flex'} textAlign={'center'} sx={{ mt: 2 }}>
                        <TextFieldRegister onChange={handleUserDataChange} required labelName={'Телефон'} idName={'phone_number'} inputProps={{pattern: '[+]?[0-9]{10,13}' }} helperText={'Моля, въведете валиден телефонен номер.'} />
                    </Grid>
                    <Grid item xs={12} sm={10} justifyContent={'center'} alignItems={'center'} display={'flex'} textAlign={'center'} sx={{ mt: 2 }}>
                        <PasswordFieldRegister onChange={handleUserDataChange} labelName={'Парола'} idName={'password'} />
                    </Grid>
                    <Grid item xs={12} sm={10} justifyContent={'center'} alignItems={'center'} display={'flex'} textAlign={'center'} sx={{ mt: 2 }}>
                        <PasswordFieldRegister
                            onChange={handleUserDataChange}
                            labelName={'Потвърждаване на паролата'}
                            idName={'password_retype'}
                            error={passwordMatchError}
                            helperText={passwordMatchError ? 'Паролите не съвпадат.' : ''}
                        />
                    </Grid>
                </Grid>

                {/* <Grid container >
                    <Grid item xs={12} sm={10} sx={{ mt: 2 }}>
                        <FormControlLabel required control={<Checkbox sx={{
                            color: globals.colors.lightRed, '&:hover': {
                                backgroundColor: 'transparent', // prevent ripple background
                                color: globals.colors.darkRed,  // change checkbox color on hover
                            },
                            '&.Mui-checked': {
                                color: globals.colors.darkRed, // keep it dark red when checked
                            },
                        }} />} 
                        label={<Typography sx={{ fontWeight: 'bold' }}>С кликането на този бутон, Вие се съгласявате с <a href="/privacy-policy" style={{ color: globals.colors.lightRed }}>Политиката за поверителност</a>.</Typography>} />
                    </Grid>
                    <Grid item xs={12} sm={8} justifyContent={'start'} alignItems={'start'} textAlign={'start'} >
                        <FormControlLabel control={<Checkbox required sx={{
                            color: globals.colors.lightRed, '&:hover': {
                                backgroundColor: 'transparent', // prevent ripple background
                                color: globals.colors.darkRed,  // change checkbox color on hover
                            },
                            '&.Mui-checked': {
                                color: globals.colors.darkRed, // keep it dark red when checked
                            },
                        }} />} label={<Typography sx={{ fontWeight: 'bold' }}>Съгласявам се с <a href="/cookies" style={{ color: globals.colors.lightRed }}>Политики за бисквитките</a>.</Typography>} />
                    </Grid>
                    <Grid item xs={12} sm={8} justifyContent={'start'} alignItems={'start'} textAlign={'start'} >
                        <FormControlLabel control={<Checkbox onChange={handleUserDataChange} id="subscribed_to_newsletter" value={userData.subscribed_to_newsletter} sx={{
                            color: globals.colors.darkBlue, '&:hover': {
                                backgroundColor: 'transparent', // prevent ripple background
                                color: globals.colors.lightBlue,  // change checkbox color on hover
                            },
                            '&.Mui-checked': {
                                color: globals.colors.lightBlue, // keep it dark red when checked
                            },
                        }} />} label={<Typography sx={{ fontWeight: 'bold' }}>Абонирайте се, за да получавате имейли с най-новите ни курсове, блогове и събития!</Typography>} />
                    </Grid>
                    <Grid item xs={12} sm={8} justifyContent={'start'} alignItems={'start'} textAlign={'start'} sx={{ mt: 3 }} >
                        <Typography sx={{ fontWeight: 'bold', color: globals.colors.darkBlue, fontSize: 18 }}>С регистрацията си, се съгласявам с <a href="/terms-and-conditions" style={{ color: globals.colors.lightRed }}>Условия за ползване на сайта</a>.</Typography>
                    </Grid>

                    <Grid item xs={12} sm={8} justifyContent={'center'} alignItems={'center'} textAlign={'center'} sx={{ mt: 5, mb: 5 }}>
                        <SignUpButton type='submit' variant='contained' size="large" sx={{ textTransform: 'capitalize', fontSize: 20 }}>Регистрация</SignUpButton>
                    </Grid>


                </Grid> */}

                <Grid container textAlign={'center'} justifyContent={'center'}>
                    <Grid item>
                        <FormGroup>
                            {/* С кликането на този бутон, Вие се съгласявате с Политиката за поверителност. */}
                            <FormControlLabel required control={<Checkbox sx={{
                                color: globals.colors.lightRed, '&:hover': {
                                    backgroundColor: 'transparent', // prevent ripple background
                                    color: globals.colors.darkRed,  // change checkbox color on hover
                                },
                                '&.Mui-checked': {
                                    color: globals.colors.darkRed, // keep it dark red when checked
                                },
                            }} />}
                                label={<Typography sx={{ fontWeight: 'bold' }}>С кликането на този бутон, Вие се съгласявате с <a href="/privacy-policy" style={{ color: globals.colors.lightRed }}>Политиката за поверителност</a>.</Typography>}
                                sx={{
                                    ".MuiFormControlLabel-asterisk": {
                                        display: 'none',
                                    },
                                    mt: 2
                                }}
                            />

                            {/* Съгласявам се с Политики за бисквитките. */}
                            <FormControlLabel control={<Checkbox required sx={{
                                color: globals.colors.lightRed, '&:hover': {
                                    backgroundColor: 'transparent', // prevent ripple background
                                    color: globals.colors.darkRed,  // change checkbox color on hover
                                },
                                '&.Mui-checked': {
                                    color: globals.colors.darkRed, // keep it dark red when checked
                                },
                            }} />} label={<Typography sx={{ fontWeight: 'bold' }}>Съгласявам се с <a href="/cookies" style={{ color: globals.colors.lightRed }}>Политики за бисквитките</a>.</Typography>}
                                sx={{
                                    ".MuiFormControlLabel-asterisk": {
                                        display: 'none',
                                    },
                                }}

                            />


                            {/* Абонирайте се, за да получавате имейли с най-новите ни курсове, блогове и събития! */}
                            <FormControlLabel control={<Checkbox onChange={handleUserDataChange} id="subscribed_to_newsletter" value={userData.subscribed_to_newsletter} sx={{
                                color: globals.colors.darkBlue, '&:hover': {
                                    backgroundColor: 'transparent', // prevent ripple background
                                    color: globals.colors.lightBlue,  // change checkbox color on hover
                                },
                                '&.Mui-checked': {
                                    color: globals.colors.lightBlue, // keep it dark red when checked
                                },
                            }} />} label={<Typography sx={{ fontWeight: 'bold' }}>Абонирайте се, за да получавате имейли с най-новите ни курсове, блогове и събития!</Typography>} />
                        </FormGroup>
                    </Grid>
                    <Grid item justifyContent={'start'} alignItems={'start'} textAlign={'start'} sx={{ mt: 3 }} >
                        <Typography sx={{ fontWeight: 'bold', color: globals.colors.darkBlue, fontSize: 18 }}>С регистрацията си, се съгласявам с <a href="/terms-and-conditions" style={{ color: globals.colors.lightRed }}>Условия за ползване на сайта</a>.</Typography>
                    </Grid>

                    <Grid item xs={12} sm={8} justifyContent={'center'} alignItems={'center'} textAlign={'center'} sx={{ mt: 5, mb: 5 }}>
                        <SignUpButton type='submit' variant='contained' size="large" sx={{ textTransform: 'capitalize', fontSize: 20 }}>Регистрация</SignUpButton>
                        {showError && <Typography sx={{ color: globals.colors.textColor, mt: 2, }} ><WarningIcon sx={{ mb: -0.6 }} />Потребителското име или имейлът вече съществуват!</Typography>}
                    </Grid>


                </Grid>
            </Grid>
        </Container>
    );
}

export default RegisterPage;